/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { BackendRoutes, LOCALISATION_VERSION } from '@myprofile-ui/myprofile-localisation-api'
import { cloneDeep } from 'lodash-es'
import { API_MOCK_LOCALISATION_BASE_URL } from '../../mock-api/constants'
import { ProfileMockApi } from '../../mock-api/mock-api.interfaces'
import { ProfileMockApiService } from '../../mock-api/mock-api.service'
import { addressLayoutData } from './layout.data'
import { addressSchemaData } from './schema.data'

@Injectable({
  providedIn: 'root',
})
export class FormServiceMockApi implements ProfileMockApi {
  private localeVersion = inject(LOCALISATION_VERSION)
  private mockApiService = inject(ProfileMockApiService)

  private readonly layoutMap = new Map<string, any>()
  private readonly schemaMap = new Map<string, any>()

  constructor() {
    // Set the mock data
    this.schemaMap.set('address', addressSchemaData)
    this.layoutMap.set('address', addressLayoutData)

    // Register the API endpoints
    this.register()
  }

  public register() {
    this._registerLocaleSchemaMocks()
    this._registerLayoutMocks()
  }

  private _registerLayoutMocks() {
    const paths = {
      de:
        `${API_MOCK_LOCALISATION_BASE_URL}` +
        BackendRoutes.FORM__LAYOUT.replace(':type', 'address')
          .replace(':country', 'de')
          .replace(':version', this.localeVersion.toUpperCase()),
      cy:
        `${API_MOCK_LOCALISATION_BASE_URL}` +
        BackendRoutes.FORM__LAYOUT.replace(':type', 'address')
          .replace(':country', 'cy')
          .replace(':version', this.localeVersion.toUpperCase()),
      fr:
        `${API_MOCK_LOCALISATION_BASE_URL}` +
        BackendRoutes.FORM__LAYOUT.replace(':type', 'address')
          .replace(':country', 'fr')
          .replace(':version', this.localeVersion.toUpperCase()),
    }

    ;(Object.keys(paths) as (keyof typeof paths)[]).forEach((country) => {
      const url = paths[country]
      this.mockApiService.onGet(url).reply(() => {
        console.log('Mocking request to ', url)
        return [200, cloneDeep(this.layoutMap.get('address')[country])]
      })
    })
  }

  private _registerLocaleSchemaMocks(): void {
    const paths = {
      de:
        `${API_MOCK_LOCALISATION_BASE_URL}` +
        BackendRoutes.FORM__LOCALE_SCHEMA.replace(':type', 'address')
          .replace(':country', 'de')
          .replace(':locale', 'de_DE')
          .replace(':version', this.localeVersion.toUpperCase()),
      cy:
        `${API_MOCK_LOCALISATION_BASE_URL}` +
        BackendRoutes.FORM__LOCALE_SCHEMA.replace(':type', 'address')
          .replace(':country', 'cy')
          .replace(':locale', 'de_DE')
          .replace(':version', this.localeVersion.toUpperCase()),
      fr:
        `${API_MOCK_LOCALISATION_BASE_URL}` +
        BackendRoutes.FORM__LOCALE_SCHEMA.replace(':type', 'address')
          .replace(':country', 'fr')
          .replace(':locale', 'de_DE')
          .replace(':version', this.localeVersion.toUpperCase()),
    }

    ;(Object.keys(paths) as (keyof typeof paths)[]).forEach((country) => {
      const url = paths[country]
      this.mockApiService.onGet(url).reply(() => {
        console.log('Mocking request to ', url)
        return [200, cloneDeep(this.schemaMap.get('address')[country])]
      })
    })
  }

  private _registerSchemaMocks(): void {
    const paths = {
      de:
        `${API_MOCK_LOCALISATION_BASE_URL}` +
        BackendRoutes.FORM__SCHEMA.replace(':version', this.localeVersion.toUpperCase())
          .replace(':type', 'address')
          .replace(':country', 'de'),
      cy:
        `${API_MOCK_LOCALISATION_BASE_URL}` +
        BackendRoutes.FORM__SCHEMA.replace(':version', this.localeVersion.toUpperCase())
          .replace(':type', 'address')
          .replace(':country', 'cy'),
    }

    ;(Object.keys(paths) as (keyof typeof paths)[]).forEach((country) => {
      const url = paths[country]
      this.mockApiService.onGet(url).reply(() => {
        console.log('Mocking request to ', url)
        return [200, cloneDeep(this.schemaMap.get('address')[country])]
      })
    })
  }
}
